import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import {
  checkApiSuccess,
  formatPrice,
  formatPercent,
  getApiData,
  isEmptyString,
  cleanNumber,
} from '@common/utils';
import {
  GridFormBox,
  GridFormItem,
  GridGuide,
  GridHeader,
  GridInput,
  GridTable,
} from '@components/common/grid';
import {
  getKrwFixedOptionPremiumRange,
  getUniversialRiderGuides,
  SIMULATION_CALC_TYPE,
} from '@common/utils/simulation';
import SatCheckBox from '@components/common/satUI/SatCheckBox';
import SatRadio from '@components/common/satUI/SatRadio';

const INTEREST_TYPE = {
  0: 'public',
  1: 'minimum',
};

const PAYMENT_PERIOD_TYPE = {
  0: 'year',
  1: 'month',
};

const PAYMENT_PERIOD_TYPE_TEXT = {
  [PAYMENT_PERIOD_TYPE[0]]: '매년',
  [PAYMENT_PERIOD_TYPE[1]]: '매월',
};

const SimulationUniversialRider = forwardRef(({ simulationInfo = {} }, ref) => {
  const {
    productInfo = {},
    planMainInfo = {},
    verifyPayload = {},
    mainProduct = {},
  } = simulationInfo;
  const { calculateDate, insuranceAge } = verifyPayload;
  const { exchangeRate } = productInfo?.productDetail ?? {};
  const isCurrencyUsd = planMainInfo?.currencyUsdYN === 'Y';

  const { error: toastError } = $hooks.useToast();
  const { alert } = $hooks.useModal();
  const { calcSimulation } = $hooks.useApi();

  const [age, setAge] = useState('');
  const [year, setYear] = useState('');
  const [period, setPeriod] = useState(PAYMENT_PERIOD_TYPE[0]);
  const [price, setPrice] = useState('');
  const [fixedPrice, setFixedPrice] = useState('');
  const [interestRate, setInterateRate] = useState(2.3); // 공시이율
  const [minimumInterest, setMinimumInterest] = useState(1); // 최저보증이율
  const [interestType, setInterestType] = useState(INTEREST_TYPE[0]); // 공시 이율
  const [ageOptions, setAgeOptions] = useState([]);
  const [periodOptions, setPeriodOptions] = useState([]);
  const [simulationData, setSimulationData] = useState({
    appGridData: [],
    lwstGridData: [],
  });
  const [oneTimePaymentMap, setOneTimePaymentMap] = useState({}); // 일시납입 금액
  const [isSummary, setIsSummary] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isFirstCalucated, setIsFirstCalculated] = useState(false);

  const setData = () => {
    const startAge = insuranceAge,
      endAge = 80;

    const ageOptions = Array.from({
      length: endAge - startAge + 1,
    }).map((_, i) => ({
      label: `${startAge + i}세`,
      value: startAge + i,
    }));
    setAgeOptions(ageOptions);
    setAge(ageOptions[0].value);

    const periodOptions = Object.values(PAYMENT_PERIOD_TYPE).map(type => ({
      label: PAYMENT_PERIOD_TYPE_TEXT[type],
      value: type,
    }));
    setPeriodOptions(periodOptions);

    setIsLoaded(true);
  };

  const checkValid = () => {
    // 주계약 보험료
    const mainProductPremium = mainProduct.premium;

    const krwFixPayinPrmum = fixedPrice;
    if (krwFixPayinPrmum !== '') {
      const parseIntAmount = Number(krwFixPayinPrmum);
      const [minPremium, maxPremium] = getKrwFixedOptionPremiumRange(
        productInfo?.productDetail,
        mainProductPremium,
      );

      if (minPremium && maxPremium) {
        //금액이 천단위 이하 금액이 나오면
        const checkValue = parseIntAmount % 1000;
        if (checkValue !== 0) {
          alert({ message: '1,000단위 미만은 입력 불가합니다.' });
          setFixedPrice(0);
          return false;
        }
        if (minPremium > parseIntAmount || parseIntAmount > maxPremium) {
          alert({ message: '100%이상 150%이하 구간에서 선택가능합니다.' });
          return false;
        }
      }
    }

    if (
      makeAdditionalPayment().length > 0 ||
      makeAdditionalPaymentList().length > 0
    ) {
      const urYearCnt = (year || 0) * 1;
      const addStartAge = (age || 0) * 1 + urYearCnt;
      const irregularAmount = makeAdditionalPaymentList().map(
        item => item.lpsumAddPayinPrmum,
      );
      const irregularAmountSum = irregularAmount.reduce((prev, cur) => {
        return prev + cur;
      }, 0);
      const irregularAge = makeAdditionalPaymentList().map(item =>
        Number(item.lpsumAddPayinAge),
      );
      const maxIrregularAge =
        irregularAge.length > 0 ? Math.max(...irregularAge) : 0;

      const selectAgeIndex =
        simulationData.appGridData.findIndex(
          item =>
            item.insrnAge ===
            (Number(addStartAge) > maxIrregularAge
              ? Number(addStartAge)
              : maxIrregularAge),
        ) + 1;

      const firstPremium = mainProductPremium; //주계약 보험료
      const limitPremium = firstPremium * 12 * selectAgeIndex;

      let applyAmount = 0;
      if (makeAdditionalPayment().length > 0) {
        const { addPayinFrqcyCd, addPayinPd, addPayinPrmum } =
          makeAdditionalPayment()[0];
        applyAmount =
          Number(addPayinPrmum.replace(',', '')) *
          addPayinPd *
          (addPayinFrqcyCd === '1' ? 1 : 12);
      }

      const compareAmount = irregularAmountSum * 1 + applyAmount;
      if (compareAmount > limitPremium) {
        alert({
          message:
            '추가납입보험료 누계가 주계약보험료 누계를 초과하는 구간이 있습니다. 다시 설계해주시기바랍니다.',
        });

        return false;
      }
    }

    return true;
  };

  const calculate = async () => {
    try {
      const isValid = checkValid();
      if (!isValid) return;

      const simulType = SIMULATION_CALC_TYPE.universialRider;

      const {
        calculateDate,
        genderCode,
        insuranceAge,
        payinMethod,
        planEstablishProduct,
      } = verifyPayload;
      const { productCode, exchangeDate, exchangeRate } =
        productInfo?.productDetail ?? {};

      const payload = {
        prmumCalcBasDd: calculateDate,
        saleProdCd: productCode.substring(0, 6),
        gndrCd: genderCode,
        riskGrdCd: 3, //전산심사 응답값으로 추가 예정
        insrnAge: insuranceAge,
        payinFrqcyCd: payinMethod,
        krwFixPayinPrmum: isEmptyString(fixedPrice) ? '0' : fixedPrice, //원화 환산 납입 보험료
        planEstablishProductDTO: planEstablishProduct.map((item, idx) => {
          // 갱신형 상품 납기 처리
          let payinPeriodValue = item.payinPeriodValue;
          if (item.riderTypes?.includes('RE')) {
            payinPeriodValue = payinPeriodValue.substring(0, 2);
          }
          return {
            riderNo: idx + 1,
            prodCd: item.productCode,
            cvrgPdTpCd: item.coveragePeriodTypeCode,
            cvrgPd: Number(item.coveragePeriodValue),
            payinPdTpCd: item.payinPeriodTypeCode,
            payinPd: payinPeriodValue,
            //anutyBgnAge: Number(item.oldIncomeAge),
            faceamnt: item.faceAmount * item.faceAmountUnit,
            calPrmum: item.premium,
          };
        }),
        businessData: exchangeDate,
        exchangeRate: Number(exchangeRate).toFixed(2),
        additionalPayment: isFirstCalucated ? makeAdditionalPayment() : [],
        additionalPaymentList: isFirstCalucated
          ? makeAdditionalPaymentList()
          : [],
      };

      const response = await calcSimulation({ simulType, payload });
      const isSuccess = checkApiSuccess(response);
      if (!isSuccess) throw new Error('시뮬레이션 계산 API 실패');

      const data = getApiData(response);

      setSimulationData(makeSimulationData(data, !isFirstCalucated));
      setIsFirstCalculated(true);

      if (data?.appltCrirt) setInterateRate(data.appltCrirt);
      if (data?.lwstGuarItrt) setMinimumInterest(data.lwstGuarItrt);
    } catch (error) {
      console.log('calculate() - error', error);
      toastError('시뮬레이션 계산에 실패하였습니다.');
    }
  };

  const makeAdditionalPayment = () => {
    return [
      {
        // PC에서 숫자인데 String 보내는 값들은 똑같이 String으로 보내도록 설정
        addPayinStrtAge: String(age),
        addPayinPd: year,
        addPayinFrqcyCd: period === PAYMENT_PERIOD_TYPE['0'] ? '1' : '2',
        addPayinPrmum: String(price),
      },
    ];
  };

  const makeAdditionalPaymentList = () => {
    const additionalPaymentList = [];

    for (const [key, value] of Object.entries(oneTimePaymentMap)) {
      if (value && value !== '') {
        const lpsumAddPayinAge = cleanNumber(key);
        const lpsumAddPayinPrmum = cleanNumber(value);
        additionalPaymentList.push({ lpsumAddPayinAge, lpsumAddPayinPrmum });
      }
    }

    return additionalPaymentList;
  };

  const makeSimulationData = (data, isFirst) => {
    const additionalPaymentList = makeAdditionalPaymentList();
    const urAdditionalPayment = simulationData.appGridData.map(i => {
      return { age: i.insrnAge, addFaceAmount1: i.addFaceAmount1 };
    });

    // 공시이율 데이터
    const appGridData =
      data?.appltCrirtPeriodicSurrenderValues
        ?.filter(i => !i.progPd.includes('개월'))
        .map((i, idx) => {
          const lumpSumPayment = additionalPaymentList.filter(
            j => j.lpsumAddPayinAge === i.insrnAge,
          )[0];
          const equalPaymetn = urAdditionalPayment.filter(
            j => i.insrnAge === j.age,
          )[0];
          if (i.progPd === '1년') {
            return {
              ...i,
              addFaceAmount1: isFirst
                ? ''
                : !equalPaymetn?.addFaceAmount1
                ? ''
                : equalPaymetn.addFaceAmount1,
              addFaceAmount2: '',
            };
          }
          return {
            ...i,
            addFaceAmount1: isFirst
              ? ''
              : !equalPaymetn?.addFaceAmount1
              ? ''
              : equalPaymetn.addFaceAmount1,
            addFaceAmount2: isFirst
              ? ''
              : !lumpSumPayment?.lpsumAddPayinPrmum
              ? ''
              : lumpSumPayment?.lpsumAddPayinPrmum,
          };
        }) ?? [];

    // 최저보증이율 데이터
    const lwstGridData =
      data?.lwstGuarItrtPeriodicSurrenderValues
        ?.filter(i => !i.progPd.includes('개월'))
        .map((i, idx) => {
          const lumpSumPayment = additionalPaymentList.filter(
            j => j.lpsumAddPayinAge === i.insrnAge,
          )[0];
          const equalPaymetn = urAdditionalPayment.filter(
            j => i.insrnAge === j.age,
          )[0];
          if (i.progPd === '1년') {
            return {
              ...i,
              addFaceAmount1: isFirst
                ? ''
                : !equalPaymetn?.addFaceAmount1
                ? ''
                : equalPaymetn.addFaceAmount1,
              addFaceAmount2: '',
            };
          }
          return {
            ...i,
            addFaceAmount1: isFirst
              ? ''
              : !equalPaymetn?.addFaceAmount1
              ? ''
              : equalPaymetn.addFaceAmount1,
            addFaceAmount2: isFirst
              ? ''
              : !lumpSumPayment?.lpsumAddPayinPrmum
              ? ''
              : lumpSumPayment?.lpsumAddPayinPrmum,
          };
        }) ?? [];

    return { appGridData, lwstGridData };
  };

  const makeGridData = (data, interestType, isSummary) => {
    const targetData =
      interestType === INTEREST_TYPE[0]
        ? data.appGridData
        : interestType === INTEREST_TYPE[1]
        ? data.lwstGridData
        : [];

    if (!isSummary) return targetData;
    else {
      return targetData
        .filter(item => !item.progPd.includes('개월'))
        .filter(item => {
          return (
            parseInt(item.progPd.padStart(4, ' ').substring(-1, 3).trim()) %
              5 ==
            0
          );
        });
    }
  };

  const makeEqualPayment = (value, options, rowObject) => {
    const { insrnAge } = rowObject;
    const startAge = age;
    const endAge = age + year;
    const isTargetAge =
      Number(startAge) < Number(insrnAge) && Number(insrnAge) <= Number(endAge);
    if (!isTargetAge) return '';

    let equalPrice = price;
    const isMonthly = period === PAYMENT_PERIOD_TYPE[1];
    if (isMonthly) equalPrice *= 12;

    return $utils.commaNumValue(equalPrice || 0);
  };

  const makeFixedPriceText = () => {
    // 주계약 보험료
    const mainProductPremium = mainProduct.premium;

    const [minPremium, maxPremium] = getKrwFixedOptionPremiumRange(
      productInfo?.productDetail,
      mainProductPremium,
    );

    const fixedPriceText = `(100% 이상 : ${formatPrice(
      minPremium,
    )}원 | 150% 이하 : ${formatPrice(maxPremium)}원)`;

    return fixedPriceText;
  };

  const handleInputChange = (value, index, rowId) => {
    const newOneTimePaymentMap = { ...oneTimePaymentMap };
    newOneTimePaymentMap[rowId] = value;
    setOneTimePaymentMap(newOneTimePaymentMap);
  };

  useEffect(setData, []);
  useEffect(() => {
    if (isLoaded) calculate();
  }, [isLoaded]);

  useImperativeHandle(ref, () => ({
    calc: () => {
      if (isLoaded) calculate();
    },
    // NOTE: 계산하기 버튼 무조건 활성화 시키고, 클릭 시 이 컴포넌트에서 체크
    checkValid: () => true,
  }));

  return (
    <>
      <GridFormBox>
        <GridFormItem label="정기 추가납입">
          <GridInput
            border
            type="select"
            width="90px"
            outerSuffix={() => <span className="txt">부터</span>}
            options={ageOptions}
            value={age}
            onChange={value => setAge(value)}
          />

          <GridInput
            border
            type="number"
            width="90px"
            outerSuffix={() => <span className="txt">년 간</span>}
            value={year}
            onChange={value => setYear(Number(value))}
          />

          <GridInput
            border
            type="select"
            width="90px"
            options={periodOptions}
            value={period}
            onChange={value => setPeriod(value)}
          />

          <GridInput
            border
            type="number"
            width="125px"
            outerSuffix={() => (
              <span className="txt">{isCurrencyUsd ? '달러' : '원'}</span>
            )}
            value={price}
            onChange={value => setPrice(Number(value))}
          />
        </GridFormItem>

        {isCurrencyUsd && (
          <GridFormItem label="원화고정납입옵션">
            <GridInput
              border
              type="number"
              width="150px"
              outerSuffix={() => <span className="txt">원</span>}
              value={fixedPrice}
              onChange={value => setFixedPrice(Number(value))}
            />

            <div>{makeFixedPriceText()}</div>
          </GridFormItem>
        )}
      </GridFormBox>

      <GridGuide
        guides={getUniversialRiderGuides({
          exchangeRate,
          interestRate,
          calculateDate,
          insuranceAge,
          isCurrencyUsd,
        })}
      ></GridGuide>

      <GridHeader title="보험료 추가납입 시 예시">
        <SatCheckBox
          value={[isSummary]}
          options={[{ value: false, label: '경과년수 요약보기' }]}
          onChange={value => setIsSummary(value)}
        />
      </GridHeader>

      <GridHeader title="수익률 가정">
        <SatRadio
          name={'interestType'}
          value={interestType}
          options={[
            {
              label: `공시이율 ${formatPercent(interestRate, 2)}%`,
              value: INTEREST_TYPE[0],
            },
            {
              label: '최저보증이율',
              value: INTEREST_TYPE[1],
            },
          ]}
          onChange={option => {
            setInterestType(option.value);
          }}
        />
      </GridHeader>

      <GridTable
        wide
        id="grid-universial-rider"
        sideTitle={isCurrencyUsd ? '(단위: 달러)' : '(단위: 원)'}
        options={{
          data: makeGridData(simulationData, interestType, isSummary),
          colNames: [
            '경과<br/>년수',
            '도달<br/>나이',
            '균등납입',
            '일시납입',
            '일반사망<br />보험금',
            '재해사망<br />보험금',
            '합계',
            '주계약',
            '특약',
            '추가납입',
            '합계',
            '주계약',
            '특약',
            '추가적립액',
            '환급률',
          ],
          colModel: [
            { name: 'progPd', rowspan: 2, sortable: false, align: 'center' },
            { name: 'insrnAge', rowspan: 2, sortable: false, align: 'center' },
            {
              name: 'addFaceAmount1',
              align: 'right',
              formatter: makeEqualPayment,
            },
            {
              name: 'addFaceAmount2',
              align: 'right',
              component: (row, rowIndex) => {
                // id(key), value 구하기
                const rowId = row.insrnAge;
                const rowValue = oneTimePaymentMap[rowId]
                  ? formatPrice(oneTimePaymentMap[rowId])
                  : '';

                // isDisabled 구하기
                const lastRow = simulationData?.appGridData.length;
                const lastYear =
                  simulationData?.appGridData[lastRow - 1]?.progPd;
                const isDisabled =
                  row.progPd.includes('개월') ||
                  row.progPd === '1년' ||
                  row.progPd === lastYear;

                return isDisabled ? (
                  <></>
                ) : (
                  <GridInput
                    type="currency"
                    currencyType={isCurrencyUsd ? 'D' : 'W'}
                    disabled={isDisabled}
                    index={rowIndex}
                    value={rowValue}
                    onChange={(value, index) =>
                      handleInputChange(cleanNumber(value), index, rowId)
                    }
                  />
                );
              },
            },
            {
              name: 'rescsnRefndAmnt1',
              rowspan: 2,
              sortable: false,
              align: 'right',
              formatter: 'integer',
            },
            {
              name: 'rescsnRefndAmnt2',
              rowspan: 2,
              sortable: false,
              align: 'right',
              formatter: 'integer',
            },
            { name: 'rescsnRefndAmnt3', align: 'right', formatter: 'integer' },
            { name: 'rescsnRefndAmnt4', align: 'right', formatter: 'integer' },
            { name: 'rescsnRefndAmnt5', align: 'right', formatter: 'integer' },
            { name: 'rescsnRefndAmnt6', align: 'right', formatter: 'integer' },
            { name: 'rescsnRefndAmnt7', align: 'right', formatter: 'integer' },
            { name: 'rescsnRefndAmnt8', align: 'right', formatter: 'integer' },
            { name: 'rescsnRefndAmnt9', align: 'right', formatter: 'integer' },
            { name: 'rescsnRefndAmnt10', align: 'right', formatter: 'integer' },
            {
              name: 'rescsnRefndAmnt11',
              rowspan: 2,
              sortable: false,
              align: 'right',
              formatter: value => `${value}%`,
            },
          ],
          multiHeaders: [
            [
              {
                startColumnName: 'addFaceAmount1',
                numberOfColumns: 2,
                titleText: '추가납입보험료',
              },
              {
                startColumnName: 'rescsnRefndAmnt3',
                numberOfColumns: 4,
                titleText: '납입보험료누계',
              },
              {
                startColumnName: 'rescsnRefndAmnt7',
                numberOfColumns: 4,
                titleText: '해약환급금',
              },
            ],
          ],
          formatter: { integer: { thousnadsSeparator: ',', defaultValue: 0 } },
          jsonReader: { repeatitems: false },
        }}
      />
    </>
  );
});

export default React.memo(SimulationUniversialRider);
